/**
 * Typed API client generated from the backend OpenAPI schema (`make types`).
 *
 * - Attaches the in-memory access token.
 * - On a 401 it refreshes once via the httpOnly cookie (`credentials: "include"`), queueing
 *   concurrent requests behind a single refresh, then replays the original request.
 * - If refresh fails the session is cleared (the auth store listens via `onSessionExpired`).
 */
import createClient, { type Middleware } from "openapi-fetch";
import { env } from "@/lib/env";
import { sessionId } from "@/lib/session";
import { toApiError } from "./errors";
import type { paths } from "./schema";

let accessToken: string | null = null;
let refreshInFlight: Promise<string | null> | null = null;
const sessionListeners = new Set<() => void>();
const replayable = new WeakMap<Request, Request>();

export const tokenStore = {
  get: () => accessToken,
  set: (token: string | null) => {
    accessToken = token;
  },
};

const subscriptionListeners = new Set<(status: string) => void>();

/** A vendor write was refused because the store's plan is locked (HTTP 402 SUBSCRIPTION_REQUIRED). */
export function onSubscriptionRequired(listener: (status: string) => void): () => void {
  subscriptionListeners.add(listener);
  return () => subscriptionListeners.delete(listener);
}

function check402(response: Response): void {
  if (response.status !== 402 || !subscriptionListeners.size) return;
  void response
    .clone()
    .json()
    .then((body: { error?: { code?: string; fields?: { status?: string } } }) => {
      if (body?.error?.code === "SUBSCRIPTION_REQUIRED") subscriptionListeners.forEach((fn) => fn(body.error?.fields?.status ?? ""));
    })
    .catch(() => undefined);
}

export function onSessionExpired(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

const REFRESH_PATH = "/auth/token/refresh/";
const NO_RETRY = [REFRESH_PATH, "/auth/login/", "/auth/register/", "/auth/logout/", "/auth/otp/verify/", "/auth/google/"];

/** Exchange the refresh cookie for a new access token. Concurrent callers share one request. */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const call = () =>
          fetch(`${env.apiBaseUrl}${REFRESH_PATH}`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", "X-Client": "web" },
            body: "{}",
          });
        let res = await call();
        if (res.status === 429) {
          // Throttled, not logged out: wait (bounded) and try once more instead of dropping the session.
          const wait = Math.min(Number(res.headers.get("Retry-After")) || 2, 5);
          await new Promise((r) => setTimeout(r, wait * 1000));
          res = await call();
        }
        if (!res.ok) {
          tokenStore.set(null);
          return null;
        }
        const data = (await res.json()) as { access: string };
        tokenStore.set(data.access);
        return data.access;
      } catch {
        return null;
      } finally {
        queueMicrotask(() => {
          refreshInFlight = null;
        });
      }
    })();
  }
  return refreshInFlight;
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    request.headers.set("X-Client", "web");
    // Guest cart key + view de-duplication. Harmless for signed-in users.
    const sid = sessionId();
    if (sid && !request.headers.has("X-Session-ID")) request.headers.set("X-Session-ID", sid);
    if (accessToken && !request.headers.has("Authorization")) {
      request.headers.set("Authorization", `Bearer ${accessToken}`);
    }
    replayable.set(request, request.clone());
    return request;
  },
  async onResponse({ request, response }) {
    check402(response);
    const url = new URL(request.url);
    const path = url.pathname.replace(new URL(env.apiBaseUrl).pathname, "");
    if (response.status !== 401 || NO_RETRY.some((p) => path.startsWith(p))) return response;

    const original = replayable.get(request);
    const token = await refreshAccessToken();
    if (!token || !original) {
      if (!token) sessionListeners.forEach((fn) => fn());
      return response;
    }
    const retry = new Request(original, { headers: new Headers(original.headers) });
    retry.headers.set("Authorization", `Bearer ${token}`);
    return fetch(retry);
  },
};

export const api = createClient<paths>({
  baseUrl: env.apiBaseUrl.replace(/\/api\/v1$/, ""),
  credentials: "include",
});
api.use(authMiddleware);

/**
 * Unwrap an openapi-fetch result: return `data` or throw a typed ApiError.
 * Usage: `const me = await unwrap(api.GET("/api/v1/me/"))`.
 */
export async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await promise;
  } catch {
    throw toApiError(0, null);
  }
  if (result.error !== undefined || !result.response.ok) {
    throw toApiError(result.response.status, result.error);
  }
  return result.data as T;
}

export type { paths };
